import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { generateClient } from "aws-amplify/api";
import { AppDispatch } from "../store";
import {
  setConnected,
  setDisconnected,
  setReconnecting,
} from "../store/subscriptionSlice";
import {
  calculateBackoffDelay,
  shouldAttemptReconnect,
  DEFAULT_RECONNECT_CONFIG,
  ReconnectConfig,
} from "../utils/subscriptionReconnect";

const client = generateClient();

// Type for the subscription observable from Amplify
interface SubscriptionObservable<TData> {
  subscribe: (handlers: {
    next: (payload: { data: TData }) => void;
    error: (err: Error) => void;
  }) => { unsubscribe: () => void };
}

interface UseSubscriptionWithReconnectOptions<TData> {
  query: string;
  variables?: Record<string, unknown>;
  onData: (data: TData) => void;
  onError?: (error: Error) => void;
  onReconnected?: () => void;
  enabled?: boolean;
  reconnectConfig?: ReconnectConfig;
}

interface UseSubscriptionWithReconnectResult {
  reconnect: () => void;
}

/**
 * Hook that wraps Amplify GraphQL subscriptions with automatic reconnection.
 * Features:
 * - Exponential backoff with jitter
 * - Redux state updates for connection status
 * - Manual reconnect function
 * - Callback on successful reconnection
 */
export function useSubscriptionWithReconnect<TData>(
  options: UseSubscriptionWithReconnectOptions<TData>
): UseSubscriptionWithReconnectResult {
  const dispatch = useDispatch<AppDispatch>();
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);
  const hasReceivedMessageRef = useRef(false);

  const {
    query,
    variables,
    onData,
    onError,
    onReconnected,
    enabled = true,
    reconnectConfig = DEFAULT_RECONNECT_CONFIG,
  } = options;

  // Keep callbacks in refs to avoid re-subscriptions
  const onDataRef = useRef(onData);
  const onErrorRef = useRef(onError);
  const onReconnectedRef = useRef(onReconnected);

  useEffect(() => {
    onDataRef.current = onData;
  }, [onData]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onReconnectedRef.current = onReconnected;
  }, [onReconnected]);

  const cleanup = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const subscribe = useCallback(() => {
    if (!enabled || !mountedRef.current) return;

    cleanup();

    try {
      console.log("[useSubscriptionWithReconnect] Creating subscription");

      const observable = client.graphql({
        query,
        variables,
      }) as unknown as SubscriptionObservable<TData>;

      const subscription = observable.subscribe({
        next: ({ data }) => {
          if (!mountedRef.current) return;

          // Track that we've received at least one message (connection confirmed)
          const wasReconnecting = attemptRef.current > 0;
          if (!hasReceivedMessageRef.current || wasReconnecting) {
            hasReceivedMessageRef.current = true;
            dispatch(setConnected());

            if (wasReconnecting) {
              console.log(
                "[useSubscriptionWithReconnect] Reconnected successfully"
              );
              onReconnectedRef.current?.();
            }
            attemptRef.current = 0;
          }

          onDataRef.current(data);
        },
        error: (err: Error) => {
          if (!mountedRef.current) return;

          console.error("[useSubscriptionWithReconnect] Subscription error:", err);
          dispatch(setDisconnected(err.message));
          onErrorRef.current?.(err);

          // Attempt reconnection
          if (shouldAttemptReconnect(attemptRef.current, reconnectConfig)) {
            const delay = calculateBackoffDelay(
              attemptRef.current,
              reconnectConfig
            );
            attemptRef.current += 1;

            console.log(
              `[useSubscriptionWithReconnect] Reconnecting in ${delay}ms (attempt ${attemptRef.current}/${reconnectConfig.maxAttempts})`
            );
            dispatch(
              setReconnecting({ attempt: attemptRef.current, nextRetryMs: delay })
            );

            reconnectTimeoutRef.current = setTimeout(() => {
              if (mountedRef.current) {
                subscribe();
              }
            }, delay);
          } else {
            console.error(
              "[useSubscriptionWithReconnect] Max reconnection attempts reached"
            );
          }
        },
      });

      subscriptionRef.current = subscription;

      // Note: We mark as connected once we receive the first message,
      // not immediately on subscription creation, to ensure the connection is truly working
    } catch (err) {
      console.error(
        "[useSubscriptionWithReconnect] Failed to create subscription:",
        err
      );
      dispatch(
        setDisconnected(err instanceof Error ? err.message : "Unknown error")
      );
    }
  }, [query, variables, enabled, cleanup, dispatch, reconnectConfig]);

  // Subscribe on mount, cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      // Mark as connected optimistically - will update if error occurs
      dispatch(setConnected());
      subscribe();
    }

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [subscribe, cleanup, enabled, dispatch]);

  // Manual reconnect function (resets attempt counter)
  const reconnect = useCallback(() => {
    console.log("[useSubscriptionWithReconnect] Manual reconnect triggered");
    attemptRef.current = 0;
    hasReceivedMessageRef.current = false;
    subscribe();
  }, [subscribe]);

  return {
    reconnect,
  };
}
